import { z } from 'zod';

export const propertySchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Property name is required'),
  address: z.string().min(1, 'Address is required'),
  type: z.enum(['Single Family', 'Duplex', 'Multifamily', 'Commercial']),
  units: z.number().int().min(0),
  occupancyRate: z.number().min(0).max(100),
  image: z.string().url().optional(),
  noi: z.number(),
  status: z.enum(['Stable', 'At Risk', 'Lease-up', 'Renovation', 'Vacant']),
  marketRent: z.number().positive().optional(),
  ownerId: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  yearBuilt: z.number().int().positive().optional(),
  bedrooms: z.number().int().positive().optional(),
  bathrooms: z.number().positive().optional(),
  sqft: z.number().int().positive().optional(),
  description: z.string().optional(),
  amenities: z.array(z.string()).optional(),
});

export const unitSchema = z.object({
  id: z.string().optional(),
  propertyId: z.string().min(1, 'Property ID is required'),
  property: z.string().min(1, 'Property name is required'),
  status: z.enum(['Vacant', 'Occupied', 'Renovation']),
  rent: z.number().positive('Rent must be positive'),
  type: z.enum(['Single Family', 'Duplex', 'Multifamily', 'Commercial']),
  sqft: z.number().int().positive().optional(),
  bedrooms: z.number().int().positive().optional(),
  bathrooms: z.number().positive().optional(),
  tenant: z.string().nullable().optional(),
});

export const tenantSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Tenant name is required'),
  unit: z.string().min(1, 'Unit is required'),
  unitId: z.string().optional(),
  property: z.string().min(1, 'Property is required'),
  propertyId: z.string().min(1, 'Property ID is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  leaseStart: z.string().min(1, 'Lease start is required'),
  leaseEnd: z.string().min(1, 'Lease end is required'),
  status: z.enum(['Current', 'Notice', 'Past']),
  balance: z.number().optional(),
  avatar: z.string().url().optional(),
});

export const vendorSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Vendor name is required'),
  trade: z.string().min(1, 'Trade is required'),
  rating: z.number().min(0).max(5),
  xp: z.number().int().min(0),
  level: z.number().int().min(0),
  completedJobs: z.number().int().min(0),
  badges: z.array(z.string()),
  avatar: z.string().url().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  status: z.enum(['Active', 'Pending', 'Suspended']).optional(),
});

export const ownerSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Owner name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  properties: z.array(z.string()),
  propertyIds: z.array(z.string()),
  status: z.string(),
  lastLogin: z.string(),
  totalUnits: z.number().int().min(0),
  avatar: z.string().url().optional(),
});

export const billSchema = z.object({
  id: z.string().optional(),
  vendor: z.string().min(1, 'Vendor name is required'),
  vendorId: z.string().min(1, 'Vendor ID is required'),
  property: z.string().min(1, 'Property name is required'),
  propertyId: z.string().min(1, 'Property ID is required'),
  amount: z.number().positive('Amount must be positive'),
  date: z.string().min(1, 'Date is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  status: z.enum(['Pending Approval', 'Approved', 'Paid', 'Overdue']),
  description: z.string().optional(),
  category: z.string().optional(),
});

export const receivableSchema = z.object({
  id: z.string().optional(),
  tenantId: z.string().min(1, 'Tenant ID is required'),
  tenant: z.string().min(1, 'Tenant name is required'),
  propertyId: z.string().min(1, 'Property ID is required'),
  property: z.string().min(1, 'Property name is required'),
  unit: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  amount: z.number(),
  type: z.enum(['Payment', 'Charge', 'Adjustment']),
  status: z.enum(['Pending', 'Paid', 'Overdue']),
  method: z.string().optional(),
  description: z.string().optional(),
});

export const ticketSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  status: z.enum(['Open', 'In Progress', 'Vendor Assigned', 'Completed', 'Verified']),
  priority: z.enum(['Low', 'Medium', 'High', 'Critical']),
  property: z.string().min(1, 'Property is required'),
  propertyId: z.string().min(1, 'Property ID is required'),
  unit: z.string().min(1, 'Unit is required'),
  unitId: z.string().optional(),
  tenantId: z.string().optional(),
  createdAt: z.string(),
  category: z.string().optional(),
});

export function validateEntity<T>(schema: z.ZodSchema<T>, data: unknown): { valid: boolean; data?: T; errors?: z.ZodError } {
  try {
    const validated = schema.parse(data);
    return { valid: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { valid: false, errors: error };
    }
    throw error;
  }
}

export function validateEntityWithErrors<T>(schema: z.ZodSchema<T>, data: unknown): { valid: boolean; data?: T; errorMessages?: string[] } {
  const result = validateEntity(schema, data);
  if (result.valid) {
    return { valid: true, data: result.data };
  }

  const errorMessages = result.errors?.errors.map(err => {
    const path = err.path.join('.');
    return path ? `${path}: ${err.message}` : err.message;
  }) ?? [];

  return { valid: false, errorMessages };
}

export function validateEntityArray<T>(schema: z.ZodSchema<T>, data: unknown): {
  valid: boolean;
  data?: T[];
  errors?: Array<{ index: number; error: z.ZodError }>;
} {
  if (schema == null || typeof (schema as { safeParse?: unknown }).safeParse !== 'function') {
    return {
      valid: false,
      errors: [{ index: -1, error: new z.ZodError([{ code: 'custom', path: [], message: 'Schema is required and must be a Zod schema with safeParse' }]) }],
    };
  }
  if (!Array.isArray(data)) {
    return {
      valid: false,
      errors: [{ index: -1, error: new z.ZodError([{ code: 'custom', path: [], message: 'Expected array' }]) }],
    };
  }

  const errors: Array<{ index: number; error: z.ZodError }> = [];
  const validatedData: T[] = [];

  data.forEach((item, index) => {
    const result = schema.safeParse(item);
    if (result.success) {
      validatedData.push(result.data);
    } else {
      errors.push({ index, error: result.error });
    }
  });

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return { valid: true, data: validatedData };
}

export const successResponseSchema = z.object({
  success: z.literal(true),
});

export const successResponseWithDataSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
  });

export function validateSuccessResponse(data: unknown): {
  valid: boolean;
  data?: { success: true };
  errors?: z.ZodError;
} {
  const result = validateEntity(successResponseSchema, data);
  return {
    valid: result.valid,
    data: result.data as { success: true } | undefined,
    errors: result.errors,
  };
}

export function validateSuccessResponseWithData<T>(
  dataSchema: z.ZodSchema<T>,
  data: unknown
): {
  valid: boolean;
  data?: { success: true; data: T };
  errors?: z.ZodError;
} {
  const schema = successResponseWithDataSchema(dataSchema);
  const result = validateEntity(schema, data);
  return {
    valid: result.valid,
    data: result.data as { success: true; data: T } | undefined,
    errors: result.errors,
  };
}

export const businessRules = {
  validateRent: (rent: number): boolean => rent > 0,

  validateLeaseDates: (startDate: string, endDate: string): boolean => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (end <= start) return false;
    if (start < today) return false;
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff > 60) return false;
    return true;
  },

  validateReceivableAmount: (amount: number, leaseMonthlyRent: number, tolerance: number = 0.01): boolean =>
    Math.abs(amount - leaseMonthlyRent) <= tolerance,

  validateBillAmount: (amount: number): boolean => amount > 0 && Number.isFinite(amount),

  validateUnitProperty: (unitPropertyId: string, propertyId: string): boolean => unitPropertyId === propertyId,

  validateUnitAvailable: (unitStatus: string): boolean => unitStatus === 'Vacant',

  validateLeaseNoOverlap: (startDate: string, endDate: string, existingLeases: Array<{ startDate: string; endDate: string; id?: string }>, excludeLeaseId?: string): boolean => {
    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);
    return !existingLeases.some(lease => {
      if (excludeLeaseId && lease.id === excludeLeaseId) return false;
      const existingStart = new Date(lease.startDate);
      const existingEnd = new Date(lease.endDate);
      return newStart < existingEnd && newEnd > existingStart;
    });
  },

  validateReceivableForLease: (leaseStatus: string): boolean => leaseStatus === 'Active',

  validateLateFeeEligible: (receivableStatus: string, receivableDate: string, daysPastDue: number = 5): boolean => {
    if (receivableStatus === 'Paid') return false;
    const dueDate = new Date(receivableDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setDate(dueDate.getDate() + daysPastDue);
    return today > dueDate;
  },

  validateBillFromWorkOrder: (workOrderStatus: string): boolean => workOrderStatus === 'Completed',

  validateJournalEntryBalance: (entries: Array<{ debitCents: number; creditCents: number }>): boolean => {
    const totalDebits = entries.reduce((sum, e) => sum + Math.trunc(e.debitCents ?? 0), 0);
    const totalCredits = entries.reduce((sum, e) => sum + Math.trunc(e.creditCents ?? 0), 0);
    return totalDebits === totalCredits;
  },

  validateEmail: (email: string): boolean => z.string().email().safeParse(email).success,

  validatePhone: (phone: string): boolean => phone.replaceAll(/\D/g, '').length >= 10,

  validatePropertyUnits: (units: number): boolean => units >= 0,

  validateEntityExists: async (entityType: string, entityId: string, db: { getProperty: (id: string) => Promise<unknown>; getTenants: () => Promise<Array<{ id: string }>>; getVendors: () => Promise<Array<{ id: string }>>; getUnit: (id: string) => Promise<unknown> }): Promise<boolean> => {
    try {
      switch (entityType) {
        case 'property':
          return !!(await db.getProperty(entityId));
        case 'tenant': {
          const tenants = await db.getTenants();
          return tenants.some((t) => t.id === entityId);
        }
        case 'vendor': {
          const vendors = await db.getVendors();
          return vendors.some((v) => v.id === entityId);
        }
        case 'unit':
          return !!(await db.getUnit(entityId));
        default:
          return false;
      }
    } catch {
      return false;
    }
  },

  validateUniqueness: async (field: string, value: string, entityType: string, excludeId?: string, db?: { getTenants: () => Promise<Array<Record<string, unknown> & { id: string }>>; getUsers: () => Promise<Array<Record<string, unknown> & { id: string }>> }): Promise<boolean> => {
    if (!db) return true;
    try {
      switch (entityType) {
        case 'tenant': {
          const tenants = await db.getTenants();
          return !tenants.some((t) => t[field] === value && t.id !== excludeId);
        }
        case 'user': {
          const users = await db.getUsers();
          return !users.some((u) => u[field] === value && u.id !== excludeId);
        }
        default:
          return true;
      }
    } catch {
      return true;
    }
  },
};

export function validateMonetaryValue(
  value: unknown,
  fieldName: string,
  options: { allowNegative?: boolean; allowZero?: boolean; maxValue?: number; minValue?: number } = {}
): void {
  const { allowNegative = false, allowZero = true, maxValue = 10000000, minValue = 0 } = options;

  if (value === null || value === undefined) return;

  if (typeof value === 'string') {
    if (value.includes('$') || (value.includes(',') && !/^\d+,\d+$/.test(value))) {
      throw new ValidationError(
        `${fieldName} must be a number, not a formatted string. Got "${value}". Remove currency formatting before saving.`,
        fieldName
      );
    }
    const parsed = Number.parseFloat(value.replaceAll(/[$,]/g, ''));
    if (isNaN(parsed)) {
      throw new ValidationError(`${fieldName} must be a number. Got "${value}" which cannot be parsed as a number.`, fieldName);
    }
    value = parsed;
  }

  if (typeof value !== 'number') {
    throw new ValidationError(`${fieldName} must be a number, got ${typeof value}`, fieldName);
  }

  if (!Number.isFinite(value)) {
    throw new ValidationError(`${fieldName} must be a finite number, got ${value}`, fieldName);
  }

  if (!allowNegative && value < 0) {
    throw new ValidationError(`${fieldName} must be positive, got ${value}`, fieldName);
  }

  if (!allowZero && value === 0) {
    throw new ValidationError(`${fieldName} must be greater than 0, got ${value}`, fieldName);
  }

  if (value < minValue) {
    throw new ValidationError(`${fieldName} must be at least ${minValue}, got ${value}`, fieldName);
  }

  if (value > maxValue) {
    throw new ValidationError(`${fieldName} exceeds maximum allowed value of ${maxValue}, got ${value}`, fieldName);
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
    public errors?: z.ZodError
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export function createValidationError(zodError: z.ZodError, entityName: string = 'Entity'): ValidationError {
  const firstError = zodError.errors[0];
  const field = firstError.path.join('.');
  const message = `${entityName} validation failed: ${field ? `${field}: ` : ''}${firstError.message}`;
  return new ValidationError(message, field, zodError);
}
